import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Avatar from '../components/Avatar';
import { useProfile } from './ProfileContext';
import { useProfileData } from './useProfileData';
import { ProfileHeader, ProfileActions, formatMemberSince } from './ProfileParts';
import { ProfileAbout, ProfileLinks, themedCardProps } from './ProfileContent';
import { useLanguage } from '../i18n/LanguageContext';
import { useLayoutMode } from '../hooks/useLayoutMode';
import '../style/Profile.css';

const MARGIN = 8;

// Desktop: a popover beside the clicked avatar/name (right of it if it fits,
// else left; clamped into the window). Phones: a bottom sheet (mobile.css).
// Closes on Esc, a click outside, or navigation (ProfileContext).
export default function ProfileCard({ username, rect, seed, onClose }) {
  const { t, locale } = useLanguage();
  const { isMobile } = useLayoutMode();
  const { openFullProfile } = useProfile();
  const { status, profile, reload } = useProfileData(username);
  const ref = useRef(null);
  const [pos, setPos] = useState(null);
  const p = profile || { username, ...(seed || {}) };
  const themed = themedCardProps(p);

  useLayoutEffect(() => {
    if (isMobile || !ref.current) return;
    const w = ref.current.offsetWidth;
    const h = ref.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = rect ? rect.right + MARGIN : (vw - w) / 2;
    if (left + w > vw - MARGIN && rect) left = rect.left - w - MARGIN;
    left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN);
    let top = rect ? rect.top : (vh - h) / 2;
    top = Math.min(Math.max(MARGIN, top), vh - h - MARGIN);
    setPos({ left, top });
  }, [isMobile, rect, status]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && ref.current.contains(e.target)) return;
      // Its own confirm dialogs and the lightbox live outside it.
      if (e.target.closest && e.target.closest('.modal-container, .modal-backdrop, .ImageLightbox')) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const showMutuals = profile && !['self', 'blocked'].includes(profile.relationship)
    && (profile.mutualFriendCount > 0 || profile.mutualGroupCount > 0);

  // Portalled to <body>: position:fixed inside a transformed/filtered
  // ancestor (the glass panels) would be relative to that panel instead.
  return createPortal(
    <>
      {isMobile && <div className="ProfileCardScrim" />}
      <div
        className={`ProfileCard${themed.className}`}
        ref={ref}
        role="dialog"
        aria-label={p.fullName || p.username}
        style={{ ...themed.style, ...(isMobile ? {} : (pos ? { left: pos.left, top: pos.top } : { visibility: 'hidden' })) }}
      >
        <ProfileHeader p={p} />
        <div className="ProfileCardBody">
          {status === 'notfound' && <p className="descGray">{t('profile.notFound')}</p>}
          {status === 'error' && <p className="descGray">{t('profile.loadFailed')}</p>}
          {p.description && <p className="ProfileBio">{p.description}</p>}
          <ProfileAbout text={p.aboutMe} />
          <ProfileLinks links={p.links} />

          {showMutuals && (
            <div className="ProfileMutuals">
              {profile.mutualFriends.length > 0 && (
                <span className="ProfileMutualAvatars">
                  {profile.mutualFriends.map((f) => (
                    <Avatar
                      key={f.username}
                      imageUrl={f.imageUrl}
                      alt={f.fullName || f.username}
                      title={f.fullName || f.username}
                      username={f.username}
                      seed={f}
                    />
                  ))}
                </span>
              )}
              <span className="descGray">
                {[
                  profile.mutualFriendCount > 0 && t('profile.mutualFriendCount', { count: profile.mutualFriendCount }),
                  profile.mutualGroupCount > 0 && t('profile.mutualGroupCount', { count: profile.mutualGroupCount })
                ].filter(Boolean).join(' · ')}
              </span>
            </div>
          )}

          {profile && profile.memberSince && (
            <div className="ProfileSince descGray">{t('profile.memberSince', { date: formatMemberSince(profile.memberSince, locale) })}</div>
          )}

          {profile && <ProfileActions profile={profile} onChanged={reload} onNavigate={onClose} compact />}

          {profile && (
            <button type="button" className="ProfileFullLink" onClick={() => openFullProfile(username)}>
              {t('profile.viewFull')}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

// Exposed for components that open a profile from something that isn't an
// <Avatar> (a name, a row).
export function useOpenProfile() {
  const { openProfile } = useProfile();
  return (username, seed) => (e) => {
    e.stopPropagation();
    openProfile(username, e.currentTarget, seed);
  };
}
